import { HeadlandIncreaseUnit, PlanType } from "@omnia/shared/generated/enums"
import { showPlanUpdateValidationDialog } from "@omnia/shared/plans/validation/validation"
import { getPlanActivity } from "../../activity"
import { CalcSeedMapCmd } from "../../calcSeedMap/calcSeedMapCmd"
import { arrayMax, Component, defineComponent, FieldViewModel, getDistinctValues, handleApiError, ICrop, IDatalayerContext, IDatalayerMapDependencies, IOmniaShell, isArrayEmpty, IVariety, omniaResolve } from "../../exports"
import { SeedRateUnit } from "../../generated/enums"
import { SeedsMap } from "../../map/seedsMap"
import { getNewPlan } from "../../plans"
import { getSeedPlanDefaults } from "../../stocks"
import { BulkAdjustmentMode, IPlanCropViewModel, ISeedRatePlan, ISeedRatePlanEditModel, ISeedsService } from "../../types"
import { getSeedRateUnitInfo, SeedRateUnitInfo } from "../../units"
import { getSeedQtyForPlan } from "../../utils"
import { EditPlanCmd } from "./editPlanCmd"
import { calculationModelOptions, seedRateUnitOptions } from "./options"
import { IBulkAdjustement } from "./types"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { showLayers } from "@omnia/shared/layers/utils"
import { validateUniqueName } from "@omnia/shared/utils/naming"
import { showOkCancel } from "@omnia/shared/shell/msgbox/msgbox"
import { ol_Feature } from "@omnia/exports/openlayers"


export class EditPlanCtrl extends Component {

    static create(): ng.IDirective {

        return defineComponent(EditPlanCtrl, "app/seeds/plan/edit/editPlan.html", {
            map: "="
        })

    }


    public readonly readOnly = this.shell.isReadOnly(p => p.seedPlans)

    private map: SeedsMap
    private _layerContext: IDatalayerContext

    public holdingCrops: ICrop[]
    public varieties: IVariety[]

    private _crop: ICrop
    private _variety: IVariety

    private _allPlans: ISeedRatePlan[]
    private _initialPlan: ISeedRatePlan
    public target: ISeedRatePlan

    public newPlan: boolean
    private planNameInUse: boolean

    private _currentLayers: LayerViewModel[]

    private form: ng.IFormController
    showValidationErrors: boolean

    public cropping: IPlanCropViewModel[]

    public bulkAdjustment: IBulkAdjustement

    private _sowingDate: Date
    public sowingDateCurrent: Date
    private _sowingDateUser: Date
    public sowingDateValidationError: string

    public targetPopulationCurrent: number
    public targetPopulation: number

    public models = calculationModelOptions
    public model: "omnia" | "establishment" | "flat" = "omnia"

    public units = seedRateUnitOptions
    public unit: SeedRateUnitInfo = getSeedRateUnitInfo(SeedRateUnit.Kg)

    public initialModel: typeof this.model

    public headland: boolean

    public headlandIncreaseUnits = getHeadlandIncreaseUnits(true)


    static $inject = ["$scope", "OmniaSeedsService", "OmniaShell", "OmniaDatalayersDependencies"]

    constructor($scope: ng.IScope, private svc: ISeedsService, private shell: IOmniaShell, private mapDeps: IDatalayerMapDependencies) {

        super($scope)

        this._icon = "icon-seeds"
    }

    protected dispose() {

        if (this.map) {
            this.map.enableMultipleSelection = false
            this.map.croppingLayer.setHiddenFeatures([])
            this.map.resetDefaultLayers()
        }

    }

    get title(): string {
        return this.newPlan ? 'Create a new seed plan' : 'Update a seed plan'
    }

    protected setup() {

        const cmd = <EditPlanCmd>this.componentContext.execArguments

        if (!cmd) {
            this.clear()
            return
        }

        this.map.enableMultipleSelection = true
        this.map.watchSelection(features => this.syncSelectionFromMap(features))
        this._layerContext = this.mapDeps.createContext(() => this.map ? this.map.getDataLayers() : [])

        this.holdingCrops = this.svc.getHoldingCrops()

        this._allPlans = this.svc.getPlans()

        this.setupTarget(cmd.plan)

        this.setActivity(getPlanActivity(this.newPlan, this.target))

    }

    private setupTarget(plan: ISeedRatePlan) {

        if (plan) {

            this.newPlan = false

            this.target = {
                ...plan,
                Maps: [...plan.Maps.map(m => ({ ...m, Layer: null }))]
            }

            this._crop = this.holdingCrops.find(c => c.CropId === plan.CropId)
            this._variety = this.svc.getVariety(plan.VarietyId)
            this.setupCropAndVariety(false)

            this._initialPlan = plan

            this.setupExistingLayers(plan)

            const qty = this.getSeedsQty()

            this.bulkAdjustment = {
                planQty: qty,
                mode: "",
                qty: qty,
                pct: 1,
                overlapPct: plan.OverlapPct
            }

        } else {

            this.newPlan = true

            this.target = plan = getNewPlan(this.svc.getNewPlanDefaults())

            this.variety = null
            this._initialPlan = null

        }

        this.model = this.initialModel = plan.UseFlatRate
            ? "flat"
            : plan.UseEstablishmentPct
                ? "establishment"
                : "omnia"

        this.unit = getSeedRateUnitInfo(plan.Unit || SeedRateUnit.Kg)

        this.headland = Boolean(plan.HeadlandWidth) && Boolean(plan.HeadlandIncrease)
        this.targetPopulationCurrent = this.targetPopulation = getCurrentTargetPopulation(plan)

        this.refreshSowingDate()

    }

    private setupCropAndVariety(setDefaults = true) {

        const { crop, variety: v, newPlan } = this

        if (crop) {

            this.varieties = this.svc.getHoldingVarieties().filter(c => c.CropId === crop.CropId)

            if (setDefaults) {

                const defaults = getSeedPlanDefaults(crop, v)

                if (defaults) {
                    this.target.GerminationRate = defaults.GerminationRate
                    this.target.TGW = defaults.TGW
                }

            }

            this.target.CropId = crop.CropId
            this.target.VarietyId = v?.CropVarietyId

            this.cropping = this.svc.getPlanCropping(this.target, true)

            for (const c of this.cropping) {

                if (newPlan) {
                    c.selectable = (!c.planId)
                }
                else {
                    c.plan = (c.planId == this.target.SeedRatePlanId ? this.target : c.plan)
                    c.selectable = (!c.planId) || (c.planId == this.target.SeedRatePlanId)
                }

            }

        }
        else {

            this.target.CropId = undefined
            this.target.VarietyId = undefined

            this.cropping = null
            this.varieties = []

        }

        this.highlightCropOrVariety()

    }

    private highlightCropOrVariety() {

        const { crop, variety } = this

        this.map.setCurrentCropAndVariety(crop?.CropId, variety?.CropVarietyId)

        if (!crop) {
            this.map.croppingLayer.setHiddenFeatures([])
            return
        }

        const planCropping = (this.cropping || []).filter(c => c.selectable)
        const otherCropping = this.svc.cropping.filter(c => planCropping.every(pc => pc.croppingVm != c))

        this.map.croppingLayer.setHiddenFeatures(this.map.findFeaturesWithData(otherCropping))

        this.syncSelectionToMap(true)

    }


    private setupExistingLayers(plan: ISeedRatePlan) {

        const layers = plan.Maps
            .filter(m => !!m.LayerId)
            .map(m => {

                const cropping = this.svc.cropping.find(pc => pc.croppingId == m.CroppingId)

                if (cropping) {
                    return new LayerViewModel(this._layerContext, this.map, m.Layer, cropping.fieldVm)
                }

            }).filter(l => !!l)

        showLayers(this._layerContext, layers)

        this._currentLayers = layers

    }

    private clear() {
        this.variety = null
        this.target = null
    }

    get yearId(): number {
        return this.svc.yearId
    }

    get crop() {
        return this._crop
    }

    set crop(value: ICrop) {
        this._crop = value
        this._variety = undefined
        this.setupCropAndVariety()
    }

    get variety(): IVariety {
        return this._variety
    }

    set variety(value: IVariety) {
        this._variety = value
        this.setupCropAndVariety()
    }

    get useFlatRate() {
        return this.model === "flat"
    }

    isUsingWeightUnit() {
        return this.unit?.type === "weight"
    }

    canChangeModel() {
        return !Boolean(this.bulkAdjustment?.mode)
    }

    canSetUnit() {
        return this.model == "flat"
    }

    canSetHeadland() {
        return this.isUsingWeightUnit()
    }

    canBulkAdjust() {
        const { newPlan, crop, model, initialModel } = this
        if (newPlan) return false
        if (!crop) return false
        if (model !== initialModel) return false
        return true
    }

    get canChangeCrop(): boolean {
        return this.newPlan
    }

    get canChangeVariety(): boolean {
        return this.newPlan
    }

    get sowingDate() {
        return this._sowingDate
    }

    set sowingDate(value: Date) {
        this._sowingDateUser = value
        this._sowingDate = value
    }

    get sowingDateChanged() {
        return !!this._sowingDateUser
    }

    hasSelectedCrops(): boolean {

        return this.cropping && this.cropping.some(c => this.isCroppingInCurrentPlan(c))

    }

    isCroppingInCurrentPlan(cropping: IPlanCropViewModel): boolean {

        return cropping && (cropping.plan === this.target)

    }

    toggleCroppingSelection(cropping: IPlanCropViewModel) {

        if (cropping.selectable && (!this.readOnly)) {
            cropping.plan = (cropping.plan == this.target ? null : cropping.plan == null ? this.target : cropping.plan)
            this.updateLayerVisibility(cropping)
            this.refreshSowingDate()
            this.syncSelectionToMap()
        }

    }

    changeCroppingSelection(select?: boolean) {

        this.cropping.filter(c => c.selectable).forEach(c => {

            if (select === true) {
                c.plan = this.target
                this.updateLayerVisibility(c)
            }
            else if (select === false) {
                c.plan = null
                this.updateLayerVisibility(c)
            }
            else {
                this.toggleCroppingSelection(c)
            }

        })

        this.refreshSowingDate()
        this.syncSelectionToMap()

    }

    onModelChanged() {

        const { target, model } = this

        const flat = model === "flat"

        if (flat) {
            target.TGW = undefined
            target.GerminationRate = undefined
            target.OverlapPct = undefined
            this.unit ||= getSeedRateUnitInfo(SeedRateUnit.Kg)
        }
        else {
            this.unit = undefined
        }

        this.headlandIncreaseUnits = getHeadlandIncreaseUnits(model !== "flat")

        if (flat && (target.HeadlandIncreaseUnit === HeadlandIncreaseUnit.SeedsM2)) {
            target.HeadlandIncreaseUnit = HeadlandIncreaseUnit.KgHa
        }


    }

    selectAll() {
        this.changeCroppingSelection(true)
    }

    unselectAll() {
        this.changeCroppingSelection(false)
    }

    reverseSelection() {
        this.changeCroppingSelection()
    }

    private syncSelectionFromMap(features: ol_Feature[]) {

        if (!this.cropping) return

        const mapSelection = features.map(f => f.get("data") as FieldViewModel)

        this.cropping.forEach(pc => {

            const inPlan = this.isCroppingInCurrentPlan(pc)
            const selected = mapSelection.indexOf(pc.croppingVm) >= 0

            if (inPlan != selected) {
                this.toggleCroppingSelection(pc)
            }

        })
    }

    private syncSelectionToMap(viewExtent?: boolean) {

        const cropping = this.cropping.filter(c => this.isCroppingInCurrentPlan(c)).map(c => c.croppingVm)

        this.map.selectFeatures(this.map.findFeaturesWithData(cropping))

        if (viewExtent) {
            this.map.viewExtentForDataItems(cropping)
        }

    }

    private updateLayerVisibility(cropping: IPlanCropViewModel) {

        if (this._currentLayers) {

            const layer = this._currentLayers.find(l => l.croppingId == cropping.croppingId)

            if (layer) {
                const visible = this.isCroppingInCurrentPlan(cropping)
                layer.visible = visible
            }

        }


    }

    private refreshSowingDate() {

        this.sowingDateCurrent = null

        const dates = getDistinctValues(
            (this.cropping || [])
                .filter(c => this.isCroppingInCurrentPlan(c))
                .map(c => c.cropping.SowingDate)
                .filter(d => !!d),
            d => d
        )

        this.sowingDateCurrent = (dates.length == 1)
            ? dates[0]
            : null

        if (this.sowingDateCurrent && (!this.sowingDateChanged)) {
            this._sowingDate = this.sowingDateCurrent
        }

    }

    private validateUniqueNames(): boolean {

        var planName = this.target.Name
        this.planNameInUse = !validateUniqueName(planName, this._allPlans, p => p.Name, p => p.SeedRatePlanId != this.target.SeedRatePlanId)

        return !(this.planNameInUse)

    }

    private validateDates() {

        this.sowingDateValidationError = null

        if (!this._sowingDateUser) return true

        const prev = this.cropping
            .filter(c => this.isCroppingInCurrentPlan(c))
            .map(c => ({
                cropping: c,
                endDate: c.cropping.EndDate,
                prevEndDate: arrayMax(c.cropping.PreviousCroppings || [], p => p.EndDate)
            }))

        let errors = prev.filter(i => i.prevEndDate > this._sowingDateUser)

        if (errors.length > 0) {
            this.sowingDateValidationError = "This sowing date cannot be used - at least one of the previous crops ends after this date"
            return false
        }

        errors = prev.filter(i => i.endDate <= this._sowingDateUser)

        if (errors.length > 0) {
            this.sowingDateValidationError = "This sowing date cannot be used - at least one of the crops is set to end before this date"
            return false
        }

        return true

    }

    canCommit(): boolean {

        if (!this.form.$valid) return false

        if (!this.validateUniqueNames()) return false
        if (!this.validateDates()) return false

        if (!this.cropping) return false
        if (!this.cropping.some(c => this.isCroppingInCurrentPlan(c))) return false

        return true

    }

    commit(): void {

        if (!this.canCommit()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        const model = this.createUpdateModel()

        const recalc = this.shouldRecalculate(model)

        this.confirmChanges(model, recalc?.new)
            .then(ok => ok
                ? this.validatePlanUpdate(model)
                : ok
            )
            .then(ok => {

                if (ok) {

                    this.svc.editPlan(model)

                        .then(r => {

                            this.svc.state.plan = r

                            if (!r.UseFlatRate) {
                                return this.autoCalculateMaps(r, recalc?.recalculate)
                            }

                        })

                        .then(() => {

                            this.cancel()

                        })

                        .catch(e => handleApiError(this.shell, e))

                }

            })

    }


    createUpdateModel(): ISeedRatePlanEditModel {

        const { target, unit, crop, variety, _initialPlan: init } = this

        target.CropId = crop.CropId
        target.VarietyId = variety?.CropVarietyId

        const useFlatRate = this.model === "flat"

        if (useFlatRate) {
            target.UseFlatRate = true
            target.UseEstablishmentPct = undefined
            target.Unit = unit?.unit ?? SeedRateUnit.Kg
        }
        else {
            target.UseFlatRate = undefined
            target.UseEstablishmentPct = (this.model === "establishment")
            target.Unit = undefined
        }

        if (!this.headland) {
            target.HeadlandWidth = undefined
            target.HeadlandIncrease = undefined
        }


        const model: ISeedRatePlanEditModel = {
            plan: target,
            croppingIds: this.cropping.filter(c => this.isCroppingInCurrentPlan(c)).map(c => c.croppingId),
            recalculate: false,
            sowingDate: this._sowingDateUser,
        }

        if (init) {

            if (init.GerminationRate != target.GerminationRate) {
                model.recalculate = true
            }

            if (this.targetPopulation != this.targetPopulationCurrent) {
                model.recalculate = true
                model.targetPopulation = this.targetPopulation
            }

            if (init.UseFlatRate != target.UseFlatRate) {
                model.recalculate = true
            }

            if (target.UseFlatRate && (init.SeedRateKgHa != target.SeedRateKgHa)) {
                model.recalculate = true
            }

            if (init.UseEstablishmentPct != target.UseEstablishmentPct) {
                model.recalculate = true
            }

            if ((init.HeadlandWidth != target.HeadlandWidth) || (init.HeadlandIncrease != target.HeadlandIncrease) || (init.HeadlandIncreaseUnit != target.HeadlandIncreaseUnit)) {
                model.recalculate = true
            }

        }

        if (this.canBulkAdjust()) {
            this.setBulkAdjustementMode(model, target.OverlapPct)
        }

        return model

    }

    setBulkAdjustementMode(model: ISeedRatePlanEditModel, overlapPct: number) {

        const b = this.bulkAdjustment
        if (!b) return

        switch (b.mode) {

            case "pct":
                model.bulkAdjustmentMode = BulkAdjustmentMode.Percentage
                model.bulkAdjustmentValue = (b.pct - 1)
                break

            case "qty":
                model.bulkAdjustmentMode = BulkAdjustmentMode.Weight
                model.bulkAdjustmentValue = b.qty
                break

        }

    }

    onHeadlandOptionChanged() {
        const { headland, target } = this
        if (headland && (!target.HeadlandIncreaseUnit)) {
            target.HeadlandIncreaseUnit = HeadlandIncreaseUnit.Pct
        }
    }

    getSeedsQty(): number {
        return getSeedQtyForPlan(this.target)
    }


    private autoCalculateMaps(plan: ISeedRatePlan, targetCrops: number[]) {

        if (isArrayEmpty(targetCrops)) {
            return omniaResolve()
        }

        const calc = new CalcSeedMapCmd(this.componentContext)

        const cropping = this.svc.cropping.filter(c => !!targetCrops.find(id => c.croppingId == id))
        calc.targetPopulation = this.targetPopulation
        calc.sowingDate = this._sowingDateUser

        return calc.calcSeedRatesMap(plan, cropping, false).result

    }


    shouldRecalculate(updateModel: ISeedRatePlanEditModel): { recalculate: number[], new: number[] } {

        if (this.newPlan) {
            return { recalculate: updateModel.croppingIds, new: updateModel.croppingIds }
        }

        const initialPlan = this._initialPlan

        if (initialPlan) {

            const newCrops = (updateModel.croppingIds.filter(id => !initialPlan.Maps.find(i => i.CroppingId == id)))

            if (newCrops.length) {
                return { recalculate: newCrops, new: newCrops }
            }

            if (initialPlan.UseEstablishmentPct != updateModel.plan.UseEstablishmentPct) {
                return { recalculate: updateModel.croppingIds, new: [] }
            }

        }

    }

    validatePlanUpdate(update: ISeedRatePlanEditModel) {

        if (!update.plan.SeedRatePlanId) {
            return true
        }

        const plan = {
            year: this.svc.yearId,
            planType: PlanType.SeedPlan,
            plans: [
                {
                    key: String(update.plan.SeedRatePlanId),
                    name: update.plan.Name,
                    cropping: (update.croppingIds ?? []).map(croppingId => ({
                        croppingId
                    }))
                }
            ]
        }

        return showPlanUpdateValidationDialog(this.svc.planValidationService, plan)

    }

    confirmChanges(updateModel: ISeedRatePlanEditModel, newCrops: number[]): ng.IPromise<boolean> {

        const initialPlan = this._initialPlan

        if ((!this.newPlan) && initialPlan) {

            const messages = []

            if (initialPlan && this.sowingDateChanged && (this.sowingDateCurrent != this._sowingDateUser)) {
                updateModel.recalculate = true
                messages.push("Existing maps will be recalculated because a new sowing date has been set.")
            }
            else if (initialPlan && (this.targetPopulation != this.targetPopulationCurrent)) {
                updateModel.recalculate = true
                messages.push("Existing maps will be recalculated because a new target plan population has been set.")
            }
            else if (updateModel.recalculate) {
                messages.push("Existing maps will be recalculated to take your changes into account.")
            }

            if (newCrops && newCrops.length) {
                messages.push("You have added new fields to this plan. New seed rates maps will be calculated for those.")
            }

            const removed = initialPlan.Maps.filter(ic => updateModel.croppingIds.indexOf(ic.CroppingId) < 0)

            if (removed.length) {

                const removedCropping = this.svc.cropping.filter(c => !!removed.find(r => r.CroppingId == c.croppingId))

                if (removedCropping.length) {

                    let msg = "The following fields were removed from this plan:<br>"
                    msg += removedCropping.map(r => "&ndash; " + r.fieldName).join("<br>")
                    msg += "<br><br>The associated seed rate maps will be deleted."
                    messages.push(msg)

                }

            }

            if (messages.length) {

                let msg = messages.map(m => "<p>" + m + "</p>").join("")
                msg += "Do you want to continue?"

                return showOkCancel(this.shell, msg, "Confirm changes")

            }

        }

        return omniaResolve(true)
    }

}

function getCurrentTargetPopulation(plan: ISeedRatePlan) {

    if (!plan) return

    const maps = plan.Maps
    if (!maps) return

    const p = getDistinctValues(maps, m => m.DefaultPopulation, p => p)

    return (p.length == 1 ? p[0] : undefined)

}

function getHeadlandIncreaseUnits(includeSeedsM2: boolean) {

    return [
        { value: HeadlandIncreaseUnit.Pct, text: "%" },
        includeSeedsM2
            ? { value: HeadlandIncreaseUnit.SeedsM2, text: "seeds/m²" }
            : null,
        { value: HeadlandIncreaseUnit.KgHa, text: "kg/ha" },
    ].filter(u => !!u)

}